// Globals
import { useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';

// Components
import { StyledContentContainer, StyledWrapper } from './DocumentsManagementContainerStyles';
import { StyledTitle } from '../../components';
import DocumentsManagement from '../../components/DocumentsManagement/DocumentsManagement';
import PropertyDocumentsList from '../../components/PropertyDocumentsList/PropertyDocumentsList';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  documentsSelector,
  deletePropertyFiles,
  getPropertyDocuments,
  getPropertyFiles,
  updatePropertyDocumentStatus,
  uploadPropertyFiles,
  getLegalDocuments,
  getSelfDeclaration,
} from '../../feature';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { FileType } from '@/models/uploads.model';

// Modules
import { uploadCommonFile } from '@/modules/Upload';
import { selectProperty } from '@/modules/Properties';

// Helpers
import { useBoolean } from '@/hooks';
import { getEntityId } from '@/utils';

type TDocumentsManagementContainerProps = {
  uploadHidden?: boolean;
  disabled?: boolean;
};

const DocumentsManagementContainer = ({
  uploadHidden = false,
  disabled = false,
}: TDocumentsManagementContainerProps) => {
  const { t } = useTranslation(['documents']);
  const dispatch = useAppDispatch();
  const { propertyDocuments, selfDeclaration, legalDocuments, files } = useAppState(documentsSelector);
  const property = useAppState(selectProperty);
  const propertyId = getEntityId(property?.PK ?? '');
  const { value: uploadedHidden, setValue: setIsUploadedHidden } = useBoolean(false);

  const handleUploadFile = async (filesToUpload: File[] | null) => {
    if (!filesToUpload?.length) return;

    const promises = filesToUpload.map((file) => dispatch(uploadCommonFile({ file, type: FileType.FILE })).unwrap());

    try {
      const uploadedFiles = await Promise.all(promises);
      return (
        uploadedFiles.map((file, idx) => ({
          name: filesToUpload[idx].name,
          link: file.asset_public_url,
        })) ?? []
      );
    } catch (error) {
      console.warn(error);
    }
  };

  const deleteDocumentsFile = async (fileId: string) => {
    dispatch(deletePropertyFiles({ fileId, propertyId }));
  };

  const updateDocumentStatus = async (documentId: string, isChecked: boolean) => {
    dispatch(updatePropertyDocumentStatus({ propertyId, documentId, is_checked: isChecked }));
  };

  const onFileUpload = useCallback(
    debounce(async (filesToUpload: File[] | null) => {
      const uploadedFiles = await handleUploadFile(filesToUpload);
      dispatch(
        uploadPropertyFiles({
          propertyId,
          files: uploadedFiles as IFile[],
          type: FileVariant.DOCUMENT_PROPERTY,
        }),
      );
    }, 500),
    [],
  );

  useEffect(() => {
    if (!propertyId) return;
    dispatch(getPropertyFiles({ propertyId }));
    dispatch(getPropertyDocuments({ propertyId }));
    dispatch(getLegalDocuments({ propertyId }));
    dispatch(getSelfDeclaration({ propertyId }));
  }, [propertyId]);

  return (
    <StyledWrapper>
      <StyledTitle>{t('documents_title')}</StyledTitle>
      <StyledContentContainer>
        <DocumentsManagement
          onFileUpload={onFileUpload}
          legalDocuments={legalDocuments ?? []}
          onDocumentDelete={deleteDocumentsFile}
          selfDeclaration={selfDeclaration}
          filesList={files ?? []}
          uploadHidden={uploadHidden}
          disabled={disabled}
        />
        <PropertyDocumentsList
          onUpdateDocumentStatus={updateDocumentStatus}
          propertyDocuments={propertyDocuments}
          uploadedHidden={uploadedHidden}
          setIsUploadedHidden={setIsUploadedHidden}
          disabled={disabled}
        />
      </StyledContentContainer>
    </StyledWrapper>
  );
};

export default DocumentsManagementContainer;
